import * as Plot from '@observablehq/plot';
import type { CustomExperimentSpec, PanelRow } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createChartCard } from '../render/chart-card';
import { plotBoxLabeled } from '../render/box-plot';
import { createDataTable, type DataTableStat } from '@learnforge/demo-kit';
import { MASTER_SURVEY, type StudentRow } from '../data/class-survey';
import { tagKey, typeKey } from '../data/columns';
import {
  fiveNumberSummary,
  tukeyOutliers,
  plotBinThresholds,
  axisTicks,
  mean,
  stdDev,
} from '../statistics';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { StatsContext, ColumnDef, DemoKey } from '../types';
import type { I18n } from '@learnforge/i18n';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface State {
  columnKey: keyof StudentRow;
  bins: number;
}

const DEFAULT_COLUMN_KEY: keyof StudentRow = 'height_cm';

// ---------------------------------------------------------------------------
// Sturges rule for bin count (module-private)
// ---------------------------------------------------------------------------

function sturges(n: number): number {
  return Math.max(5, Math.ceil(Math.log2(n) + 1));
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, DemoKey, StatsContext> {
  return {
    labelKey: 'exp.survey-explorer.title',
    demoId: 'statistics',
    advancedKey: 'survey-explorer',

    initialState(): State {
      return {
        columnKey: DEFAULT_COLUMN_KEY,
        bins: sturges(MASTER_SURVEY.rows.length),
      };
    },

    panel: (state, ctx) => {
      const col = MASTER_SURVEY.columns.find((c) => c.key === state.columnKey)!;
      const rows: PanelRow<DemoKey, State>[] = [
        {
          kind: 'dropdown',
          id: 'sx-dataset',
          labelKey: 'param.dataset.label',
          options: [{ value: MASTER_SURVEY.id, labelKey: MASTER_SURVEY.labelKey }],
          value: MASTER_SURVEY.id,
          onChange: () => undefined,
        },
        {
          kind: 'dropdown',
          id: 'sx-column',
          labelKey: 'param.column.label',
          options: MASTER_SURVEY.columns.map((c) => ({ value: c.key, labelKey: c.labelKey })),
          value: state.columnKey,
          onChange: (v) => ({
            columnKey: v as keyof StudentRow,
            bins: sturges(MASTER_SURVEY.rows.length),
          }),
        },
        { kind: 'custom', render: () => makeTypeChip(col, ctx.i18n) },
      ];
      if (col.type === 'quantitative-continuous') {
        rows.push({
          kind: 'slider',
          id: 'sx-bins',
          labelKey: 'param.bins.label',
          min: 5,
          max: 30,
          step: 1,
          value: state.bins,
          format: (v) => String(v),
          onChange: (v) => (prev) => ({ ...prev, bins: v }),
        });
      }
      return rows;
    },

    createRender(ctx, host) {
      // ── DOM: grid ──
      const grid = document.createElement('div');
      grid.className = 'stats-grid';
      host.appendChild(grid);

      // `getStats()` below reads this on every table refresh; `update()` keeps
      // it in sync with the live column before calling `dataTable.update()`.
      let currentColumnKey: keyof StudentRow = DEFAULT_COLUMN_KEY;
      let visibleColumnKey: keyof StudentRow | null = null;

      const dataTable = createDataTable<DemoKey>({
        i18n: ctx.i18n,
        columns: MASTER_SURVEY.columns.map((col) => ({
          id: col.key,
          labelKey: col.labelKey,
          ...(col.type === 'qualitative-multi'
            ? {
                format: (v: unknown) =>
                  (v as readonly string[]).map((tag) => tagLabel(col, tag, ctx.i18n)).join(', '),
              }
            : {}),
        })),
        getRows: () => MASTER_SURVEY.rows as unknown as ReadonlyArray<Record<string, unknown>>,
        getStats: () => columnStats(currentColumnKey, ctx.i18n),
        defaultVisible: [DEFAULT_COLUMN_KEY],
        rowNumbers: true,
      });

      // ── DOM: 4 chart cards (built once) — DOM order: data table, tl, tr, br ──
      const cards = {
        table: createChartCard<DemoKey>({
          titleKey: 'table.data',
          i18n: ctx.i18n,
          headerActions: [dataTable.controls],
          collapsible: { storageKey: 'learnforge:collapsible:statistics:survey-explorer:table' },
        }),
        tl: createChartCard<DemoKey>({
          titleKey: 'card.histogram',
          i18n: ctx.i18n,
          collapsible: { storageKey: 'learnforge:collapsible:statistics:survey-explorer:tl' },
        }),
        tr: createChartCard<DemoKey>({
          titleKey: 'card.cumulative',
          i18n: ctx.i18n,
          collapsible: { storageKey: 'learnforge:collapsible:statistics:survey-explorer:tr' },
        }),
        br: createChartCard<DemoKey>({
          titleKey: 'card.summary',
          i18n: ctx.i18n,
          collapsible: { storageKey: 'learnforge:collapsible:statistics:survey-explorer:br' },
        }),
      };
      grid.append(cards.table.element, cards.tl.element, cards.tr.element, cards.br.element);
      cards.table.setBody(dataTable.element);

      // ── RenderHandle ──
      const update = (state: State): void => {
        const { columnKey, bins } = state;

        currentColumnKey = columnKey;
        if (columnKey !== visibleColumnKey) {
          dataTable.setVisible([columnKey]);
          visibleColumnKey = columnKey;
        }
        dataTable.update();

        const col = MASTER_SURVEY.columns.find((c) => c.key === columnKey) as ColumnDef<StudentRow>;
        const values = columnValues(columnKey);
        const isQuant =
          col.type === 'quantitative-continuous' || col.type === 'quantitative-discrete';

        cards.tl.setBody(renderTopLeft(col, values, bins));
        cards.tr.element.hidden = !isQuant;
        cards.br.element.hidden = !isQuant;
        if (isQuant) {
          cards.tr.setBody(
            col.type === 'quantitative-continuous'
              ? plotCumulative(values as number[], bins)
              : plotCumulativeDiscrete(values as number[]),
          );
          cards.br.setBody(makeFiveNumCard(values as number[], ctx.i18n));
        }

        // Update card titles to match the active type
        updateCardTitles(col.type, cards);

        // CRITICAL: signal VR readiness on all 4 cards after every render
        for (const c of Object.values(cards)) {
          c.setBusy(false);
        }
      };

      return {
        update,
        dispose() {
          // Destroy the data table (unsubscribes i18n + tears down its popovers)
          // and the 4 cards — do NOT touch ctx.panel or ctx.actionBar.
          dataTable.destroy();
          for (const c of Object.values(cards)) {
            c.destroy();
          }
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// ---------------------------------------------------------------------------
// Back-compat alias (keeps index.ts + test green without editing either)
// ---------------------------------------------------------------------------

export const mountSurveyExplorer = experiment.mount.bind(experiment);

// ---------------------------------------------------------------------------
// Column-value helpers
// ---------------------------------------------------------------------------

function columnValues(columnKey: keyof StudentRow): unknown[] {
  return MASTER_SURVEY.rows.map(
    (r) => (r as unknown as Record<string, unknown>)[columnKey as string],
  );
}

/** Mode (most frequent value(s)) over a flat list of strings/tags. */
function computeMode(values: readonly string[]): { modes: string[]; count: number } {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let maxCount = 0;
  const modes: string[] = [];
  for (const [v, c] of counts) {
    if (c > maxCount) {
      maxCount = c;
      modes.length = 0;
      modes.push(v);
    } else if (c === maxCount) {
      modes.push(v);
    }
  }
  return { modes, count: maxCount };
}

function tagLabel(col: ColumnDef<StudentRow>, tag: string, i18n: I18n<DemoKey>): string {
  return i18n.t(col.tagLabelKey ? col.tagLabelKey(tag) : tagKey(tag));
}

/** Stats popover contents for the data table — varies by column type. */
function columnStats(columnKey: keyof StudentRow, i18n: I18n<DemoKey>): DataTableStat[] {
  const col = MASTER_SURVEY.columns.find((c) => c.key === columnKey) as ColumnDef<StudentRow>;
  const values = columnValues(columnKey);

  if (col.type === 'quantitative-continuous' || col.type === 'quantitative-discrete') {
    const nums = values as number[];
    const s = fiveNumberSummary(nums);
    const outliers = tukeyOutliers(nums);
    const outlierValue =
      outliers.outlierIndices.length > 0
        ? outliers.outlierIndices
            .map((i) => formatNumber(nums[i]!, { decimals: 2, trimIntegers: true }))
            .join(', ')
        : '—';
    return [
      { label: i18n.t('stat.n'), value: String(nums.length) },
      {
        label: i18n.t('stat.mean'),
        value: formatNumber(mean(nums), { decimals: 2, trimIntegers: true }),
      },
      {
        label: i18n.t('stat.median'),
        value: formatNumber(s.median, { decimals: 2, trimIntegers: true }),
      },
      {
        label: i18n.t('stat.sd'),
        value: formatNumber(stdDev(nums), { decimals: 2, trimIntegers: true }),
      },
      { label: i18n.t('stat.q1'), value: formatNumber(s.q1, { decimals: 2, trimIntegers: true }) },
      { label: i18n.t('stat.q3'), value: formatNumber(s.q3, { decimals: 2, trimIntegers: true }) },
      {
        label: i18n.t('stat.iqr'),
        value: formatNumber(s.q3 - s.q1, { decimals: 2, trimIntegers: true }),
      },
      { label: i18n.t('stat.outliers'), value: outlierValue },
    ];
  }

  if (col.type === 'qualitative-multi') {
    const multiValues = values as readonly (readonly string[])[];
    const flat = multiValues.flatMap((v) => [...v]);
    const { modes } = computeMode(flat);
    return [
      { label: i18n.t('stat.n'), value: String(multiValues.length) },
      {
        label: i18n.t('stat.mode'),
        value: modes.map((tag) => tagLabel(col, tag, i18n)).join(', '),
      },
      { label: i18n.t('stat.distinct'), value: String(new Set(flat).size) },
    ];
  }

  const strs = values as string[];
  const { modes } = computeMode(strs);
  return [
    { label: i18n.t('stat.n'), value: String(strs.length) },
    { label: i18n.t('stat.mode'), value: modes.join(', ') },
    { label: i18n.t('stat.distinct'), value: String(new Set(strs).size) },
  ];
}

// ---------------------------------------------------------------------------
// Type-detection chip (panel custom row)
// ---------------------------------------------------------------------------

function makeTypeChip(col: ColumnDef<StudentRow>, i18n: I18n<DemoKey>): HTMLElement {
  const chip = document.createElement('div');
  chip.className = 'sx-type-chip';
  const label = document.createElement('span');
  label.className = 'sx-chip-label';
  label.textContent = i18n.t(typeKey(col.type, 'label'));
  const def = document.createElement('span');
  def.className = 'sx-chip-def';
  def.textContent = i18n.t(typeKey(col.type, 'def'));
  chip.append(label, def);
  return chip;
}

// ---------------------------------------------------------------------------
// Card title helpers
// ---------------------------------------------------------------------------

function updateCardTitles(
  type: ColumnDef<StudentRow>['type'],
  cards: {
    tl: { setTitleKey(k: DemoKey): void };
    tr: { setTitleKey(k: DemoKey): void };
    br: { setTitleKey(k: DemoKey): void };
  },
): void {
  switch (type) {
    case 'quantitative-continuous':
      cards.tl.setTitleKey('card.histogram');
      cards.tr.setTitleKey('card.cumulative');
      cards.br.setTitleKey('card.summary');
      break;
    case 'quantitative-discrete':
      cards.tl.setTitleKey('card.frequency');
      cards.tr.setTitleKey('card.cumulative');
      cards.br.setTitleKey('card.summary');
      break;
    case 'qualitative-ordinal':
    case 'qualitative-nominal':
      cards.tl.setTitleKey('card.bar');
      break;
    case 'qualitative-multi':
      cards.tl.setTitleKey('card.tag-frequency');
      break;
  }
}

// ---------------------------------------------------------------------------
// Top-left card renderer (always shown — histogram/bar/tag-frequency by type)
// ---------------------------------------------------------------------------

function renderTopLeft(
  col: ColumnDef<StudentRow>,
  values: unknown[],
  bins: number,
): HTMLElement | SVGSVGElement {
  switch (col.type) {
    case 'quantitative-continuous':
      return plotHistogram(values as number[], bins);
    case 'quantitative-discrete':
      return plotHistogramDiscrete(values as number[]);
    case 'qualitative-ordinal':
      return plotOrderedBar(values as string[], col.ordinalOrder as readonly string[]);
    case 'qualitative-nominal':
      return plotBarNominal(values as string[]);
    case 'qualitative-multi':
      return plotTagFreqBar(values as readonly string[][], col.tags as readonly string[]);
  }
}

// ---------------------------------------------------------------------------
// Plot helpers — Observable Plot
// ---------------------------------------------------------------------------

/**
 * Shared x-axis config for the histogram and cumulative-frequency plots:
 * ticks sit on the bin edges (thinned above 12 via {@link axisTicks}) so the
 * axis lines up with the bars/steps instead of Plot's automatic "nice"
 * ticks. Degenerate (empty) edge lists fall back to Plot's default ticks.
 */
function histogramAxisX(edges: readonly number[]): {
  ticks?: number[];
  tickFormat?: (d: number) => string;
} {
  if (edges.length === 0) return {};
  return {
    ticks: axisTicks(edges),
    tickFormat: (d: number) => formatNumber(d, { decimals: 1, trimIntegers: true }),
  };
}

function plotHistogram(nums: number[], bins: number): SVGSVGElement | HTMLElement {
  const edges = plotBinThresholds(nums, bins);
  return Plot.plot({
    x: histogramAxisX(edges),
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.rectY(nums, Plot.binX({ y: 'count' }, { x: (d: number) => d, thresholds: edges })),
      Plot.ruleY([0]),
    ],
    marginBottom: 30,
    height: 220,
  }) as SVGSVGElement | HTMLElement;
}

function plotCumulative(nums: number[], bins: number): HTMLElement {
  const sorted = [...nums].sort((a, b) => a - b);
  const pairs = sorted.map((v, i) => ({ x: v, y: i + 1 }));
  const edges = plotBinThresholds(nums, bins);
  return Plot.plot({
    x: histogramAxisX(edges),
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.frame({ anchor: 'bottom' }),
      Plot.line(pairs, { x: 'x', y: 'y', stroke: 'steelblue', strokeWidth: 2 }),
    ],
    marginBottom: 30,
    height: 220,
  }) as HTMLElement;
}

/**
 * Discrete-column histogram: one bar per distinct observed value (no
 * binning math). A band scale over the sorted distinct values keeps each
 * x-axis label centered under its column, showing the value it represents.
 */
function plotHistogramDiscrete(nums: number[]): SVGSVGElement | HTMLElement {
  const domain = [...new Set(nums)].sort((a, b) => a - b);
  return Plot.plot({
    x: { domain },
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.barY(nums, Plot.groupX({ y: 'count' }, { x: (d: number) => d })),
      Plot.ruleY([0]),
    ],
    marginBottom: 30,
    height: 220,
  }) as SVGSVGElement | HTMLElement;
}

/**
 * Discrete-column cumulative frequency: same step line as the continuous
 * chart, but without bin-edge ticks — there are no bins, so Plot's default
 * axis applies.
 */
function plotCumulativeDiscrete(nums: number[]): HTMLElement {
  const sorted = [...nums].sort((a, b) => a - b);
  const pairs = sorted.map((v, i) => ({ x: v, y: i + 1 }));
  return Plot.plot({
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.frame({ anchor: 'bottom' }),
      Plot.line(pairs, { x: 'x', y: 'y', stroke: 'steelblue', strokeWidth: 2 }),
    ],
    marginBottom: 30,
    height: 220,
  }) as HTMLElement;
}

function plotOrderedBar(strs: string[], order: readonly string[]): SVGSVGElement | HTMLElement {
  return Plot.plot({
    x: { domain: order },
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.barY(strs, Plot.groupX({ y: 'count' }, { x: (d: string) => d })),
      Plot.ruleY([0]),
    ],
    marginBottom: 40,
    height: 220,
  }) as SVGSVGElement | HTMLElement;
}

function plotBarNominal(strs: string[]): SVGSVGElement | HTMLElement {
  return Plot.plot({
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.barY(strs, Plot.groupX({ y: 'count' }, { x: (d: string) => d })),
      Plot.ruleY([0]),
    ],
    marginBottom: 40,
    height: 220,
  }) as SVGSVGElement | HTMLElement;
}

function plotTagFreqBar(
  multiValues: readonly string[][],
  tags: readonly string[],
): SVGSVGElement | HTMLElement {
  // Flatten: each row contributes each tag it contains
  const flat: string[] = [];
  for (const row of multiValues) {
    for (const tag of row) {
      flat.push(tag);
    }
  }
  return Plot.plot({
    x: { domain: tags },
    marks: [
      Plot.frame({ anchor: 'left' }),
      Plot.barY(flat, Plot.groupX({ y: 'count' }, { x: (d: string) => d })),
      Plot.ruleY([0]),
    ],
    marginBottom: 40,
    height: 220,
  }) as SVGSVGElement | HTMLElement;
}

// ---------------------------------------------------------------------------
// Text-card helpers
// ---------------------------------------------------------------------------

function makeFiveNumCard(nums: number[], i18n: I18n<DemoKey>): HTMLElement {
  const summary = fiveNumberSummary(nums);
  const outliers = tukeyOutliers(nums);

  const card = document.createElement('div');
  card.className = 'sx-text-card';

  card.appendChild(plotBoxLabeled(nums));

  const rows: [string, string][] = [
    [i18n.t('stat.min'), formatNumber(summary.min, { decimals: 2, trimIntegers: true })],
    [i18n.t('stat.q1'), formatNumber(summary.q1, { decimals: 2, trimIntegers: true })],
    [i18n.t('stat.median'), formatNumber(summary.median, { decimals: 2, trimIntegers: true })],
    [i18n.t('stat.q3'), formatNumber(summary.q3, { decimals: 2, trimIntegers: true })],
    [i18n.t('stat.max'), formatNumber(summary.max, { decimals: 2, trimIntegers: true })],
    [
      i18n.t('stat.iqr'),
      formatNumber(summary.q3 - summary.q1, { decimals: 2, trimIntegers: true }),
    ],
  ];

  const table = document.createElement('table');
  table.className = 'sx-summary-table';
  for (const [label, value] of rows) {
    const tr = document.createElement('tr');
    const td1 = document.createElement('td');
    td1.textContent = label;
    const td2 = document.createElement('td');
    td2.textContent = value;
    tr.append(td1, td2);
    table.appendChild(tr);
  }
  card.appendChild(table);

  if (outliers.outlierIndices.length > 0) {
    const p = document.createElement('p');
    p.className = 'sx-outlier-note';
    p.textContent = `${i18n.t('stat.outliers')}: ${outliers.outlierIndices
      .map((i) => formatNumber(nums[i]!, { decimals: 1, trimIntegers: true }))
      .join(', ')}`;
    card.appendChild(p);
  }

  return card;
}
